import {useEffect, useRef, useState} from 'react';

/**
 * Custom hook to optimize dependency updates by serializing dependency value.
 *
 * @template T - The type of the dependency value.
 * @param {T} value - The dependency value that needs to be optimized.
 * @param {(value: T) => string} serializeDependency - A function to serialize the dependency value into a string.
 * @returns {[T, React.MutableRefObject<string>]} - Returns the saved dependency value and a ref to the serialized value.
 *
 * This hook is useful when you need to minimize unnecessary updates in a component
 * due to complex dependency values that may not change in a meaningful way.
 * It uses serialization to determine if the dependency value has changed.
 */

export const useOptimizedDependency = <T>(value: T, serializeDependency: (value: T) => string) => {
  const serializedValue = useRef(serializeDependency(value));
  const [savedValue, setSavedValue] = useState<T>(value);

  useEffect(() => {
    const serializedDependencyValue = serializeDependency(value);
    if (serializedValue.current !== serializedDependencyValue) {
      serializedValue.current = serializedDependencyValue;
      setSavedValue(value);
    }
  }, [serializeDependency, value]);

  return savedValue;
};
